import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  Tabs,
  Tab,
  Snackbar,
  Alert,
  useMediaQuery,
  useTheme,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { usePermissions } from '../hooks/usePermissions';
import RawMaterial from '../components/RawMaterial';
import InventoryStatus from '../components/InventoryStatus';
import Shops from '../components/Shops';
import Items from '../components/Items';
import Users from '../components/Users';
import Vendors from '../components/Vendors';
import Requests from '../components/Requests';
import InventoryReport from '../components/InventoryReport';
import POS from '../components/POS';
import Bills from '../components/Bills';
import Invoices from '../components/Invoices';
import DataView from '../components/DataView';
import CardDiscounts from '../components/CardDiscounts';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const username = localStorage.getItem('username') || 'User';
  const role = localStorage.getItem('role') || '';
  const token = localStorage.getItem('token') || '';
  
  const [activeTab, setActiveTab] = useState(0);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState<'success' | 'error'>('success');
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(null);

  const { hasPermission, canAccessTab } = usePermissions(role);

  // Define tab configurations
  const tabs = [
    { id: 'inventory-status', label: 'Inventory Status' },
    { id: 'shops', label: 'Shops' },
    { id: 'items', label: 'Items' },
    { id: 'users', label: 'Users' },
    { id: 'vendors', label: 'Vendors' },
    { id: 'requests', label: 'Requests' },
    { id: 'inventory-report', label: 'Inventory Report' },
    { id: 'pos', label: 'POS' },
    { id: 'bills', label: 'Bills' },
    { id: 'invoices', label: 'Invoices' },
    { id: 'raw-material', label: 'Raw Material' },
    { id: 'data-view', label: 'Data View' },
    { id: 'card-discounts', label: 'Card Discounts' }
  ];

  const visibleTabs = tabs.filter(tab => canAccessTab(tab.id));

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  const handleMobileTabSelect = (tabIndex: number) => {
    setActiveTab(tabIndex);
    setMobileMenuAnchor(null);
  };

  const showSuccess = (message: string) => {
    setToastMessage(message);
    setToastSeverity('success');
    setShowToast(true);
  };

  const showError = (message: string) => {
    setToastMessage(message);
    setToastSeverity('error');
    setShowToast(true);
  };

  const renderTabContent = () => {
    const tabId = visibleTabs[activeTab]?.id;
    switch (tabId) {
      case 'inventory-status':
        return <InventoryStatus />;
      case 'shops':
        return <Shops />;
      case 'items':
        return <Items />;
      case 'users':
        return <Users />;
      case 'vendors':
        return <Vendors />;
      case 'requests':
        return <Requests />;
      case 'inventory-report':
        return <InventoryReport />;
      case 'pos':
        return <POS />;
      case 'bills':
        return <Bills />;
      case 'invoices':
        return <Invoices />;
      case 'raw-material':
        return <RawMaterial />;
      case 'data-view':
        return <DataView />;
      case 'card-discounts':
        return <CardDiscounts />;
      default:
        return null;
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('username');
    localStorage.removeItem('role');
    localStorage.removeItem('shopName');
    navigate('/login');
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static">
        <Toolbar>
          <Typography 
            variant={isMobile ? "subtitle1" : "h6"} 
            component="div" 
            sx={{ 
              flexGrow: 1,
              fontSize: isMobile ? '0.9rem' : '1.25rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {isMobile ? `Welcome, ${username}` : `Inventory Management System - Welcome, ${username}`}
          </Typography>
          
          {/* Mobile Tab Selection in Header */}
          {isMobile && (
            <Button
              variant="outlined"
              onClick={handleMobileMenuOpen}
              sx={{
                color: 'white',
                borderColor: 'rgba(255,255,255,0.3)',
                textTransform: 'none',
                fontSize: '0.8rem',
                fontWeight: 'bold',
                mr: 2,
                px: 2,
                py: 0.5,
                minWidth: 'auto',
                '&:hover': {
                  borderColor: 'rgba(255,255,255,0.5)',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                }
              }}
            >
              {visibleTabs[activeTab]?.label || 'Select Tab'}
            </Button>
          )}
          
          <Button color="inherit" onClick={handleLogout} size={isMobile ? "small" : "medium"}>
            {isMobile ? "Logout" : "Logout"}
          </Button>
        </Toolbar>
      </AppBar>
      
      <Container maxWidth="xl" sx={{ mt: 2, px: isMobile ? 1 : 3 }}>
        {/* Mobile Tab Menu */}
        {isMobile && (
          <Menu
            anchorEl={mobileMenuAnchor}
            open={Boolean(mobileMenuAnchor)}
            onClose={handleMobileMenuClose}
            PaperProps={{
              style: {
                maxHeight: '70vh',
                width: '100%',
                maxWidth: '280px'
              }
            }}
          >
            {visibleTabs.map((tab, index) => (
              <MenuItem 
                key={tab.id} 
                onClick={() => handleMobileTabSelect(index)}
                selected={activeTab === index}
                sx={{ 
                  py: 1.5,
                  fontSize: '1rem'
                }}
              >
                <ListItemText primary={tab.label} />
              </MenuItem>
            ))}
          </Menu>
        )}

        {!isMobile && (
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={activeTab} 
              onChange={handleTabChange} 
              aria-label="dashboard tabs"
              variant="scrollable"
              scrollButtons="auto"
            >
              {visibleTabs.map((tab, index) => (
                <Tab 
                  key={tab.id} 
                  label={tab.label}
                  sx={{ 
                    minWidth: 'auto',
                    px: 2,
                    fontSize: '0.875rem'
                  }}
                />
              ))}
            </Tabs>
          </Box>
        )}
        
        <Box sx={{ mt: 2 }}>
          {renderTabContent()}
        </Box>
      </Container>

      <Snackbar
        open={showToast}
        autoHideDuration={6000}
        onClose={() => setShowToast(false)}
      >
        <Alert onClose={() => setShowToast(false)} severity={toastSeverity}>
          {toastMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Dashboard;
